/**
 * StepSpaces - Wizard Step 4: Vessel spaces and accommodation
 * Preserves all logic from VesselSpacesTab including critical galley clearing logic
 */

import React, { useMemo, useEffect } from 'react';
import { Home, Utensils, AlertTriangle } from 'lucide-react';
import { Switch } from '../../../../catalyst/checkbox';
import WizardSection from '../WizardSection';
import AdditionalRiskIdentification from './components/AdditionalRiskIdentification';

const StepSpaces = ({ draftConfig, onUpdate }) => {
  // Create updateConfig function compatible with existing logic
  const updateConfig = (updates) => {
    if (typeof updates === 'function') {
      onUpdate(updates(draftConfig));
    } else {
      onUpdate(updates);
    }
  };

  return (
    <WizardSection>
      {/* Step Introduction */}
      <div className="text-center mb-6">
        <div className="mx-auto flex items-center justify-center h-14 w-14 md:h-12 md:w-12 rounded-full bg-green-100 mb-3">
          <Home className="h-7 w-7 md:h-6 md:w-6 text-green-600" />
        </div>
        <h2 className="text-xl md:text-xl font-bold text-gray-900 mb-2">
          Spaces & Accommodation
        </h2>
        <WizardSection.Text className="max-w-2xl mx-auto">
          Configure your vessel's spaces, accommodation, and galley facilities. These determine fire detection, 
          ventilation, and safety equipment requirements.
        </WizardSection.Text>
      </div>

      {/* Regulatory Context */}
      <WizardSection.Callout variant="regulatory" title="WBC3 Accommodation Requirements">
        Accommodation spaces (including galleys) require specific fire detection and ventilation per WBC3. 
        Cooking facilities with open flames have additional fire protection requirements per Section 16.3.1.
      </WizardSection.Callout>

      {/* Vessel Spaces */}
      <WizardSection.Card>
        <WizardSection.Header>Vessel Spaces</WizardSection.Header>
        
        <div className="space-y-4">
          <div>
            <label className="flex items-center justify-between">
              <div className="flex-1">
                <span className="text-sm font-medium text-gray-900">Enclosed Spaces Present</span>
                <p className="text-xs text-gray-500 mt-1">Vessel has enclosed spaces requiring entry procedures</p>
              </div>
              <Switch
                checked={draftConfig.hasEnclosedSpaces || draftConfig.enclosed_spaces_present || false}
                onChange={(checked) => updateConfig({ 
                  hasEnclosedSpaces: checked, 
                  enclosed_spaces_present: checked 
                })}
              />
            </label>
          </div>

          <div>
            <label className="flex items-center justify-between">
              <div className="flex-1">
                <span className="text-sm font-medium text-gray-900">Wheelhouse/Pilot House</span>
                <p className="text-xs text-gray-500 mt-1">Has wheelhouse or pilot house (counts as substantial enclosure)</p>
              </div>
              <Switch
                checked={draftConfig.hasWheelhouse || false}
                onChange={(checked) => updateConfig({ hasWheelhouse: checked })}
              />
            </label>
          </div>

          <div>
            <label className="flex items-center justify-between">
              <div className="flex-1">
                <span className="text-sm font-medium text-gray-900">Accommodation Spaces (Sleeping/Living Quarters)</span>
                <p className="text-xs text-gray-500 mt-1">
                  Check if vessel has sleeping berths or dedicated living quarters.
                  Note: Galleys are also accommodation spaces per WBC3, but can be tracked separately below.
                </p>
              </div>
              <Switch
                checked={draftConfig.hasAccommodation || false}
                onChange={(checked) => updateConfig({
                  hasAccommodation: checked,
                  hasCabin: checked,  // If has accommodation, likely has cabin
                  enclosed_accommodation_present: checked
                })}
              />
            </label>
          </div>

          <div>
            <label className="flex items-center justify-between">
              <div className="flex-1">
                <span className="text-sm font-medium text-gray-900">Machinery Space Present</span>
                <p className="text-xs text-gray-500 mt-1">Vessel has dedicated machinery space (affects fire detection requirements)</p>
              </div>
              <Switch
                checked={draftConfig.hasMachinerySpace || false}
                onChange={(checked) => updateConfig({ hasMachinerySpace: checked })}
              />
            </label>
          </div>
        </div>
      </WizardSection.Card>

      {/* Galley Configuration */}
      <WizardSection.Card>
        <WizardSection.Header>
          <div className="flex items-center">
            <Utensils className="h-5 w-5 text-gray-400 mr-2" />
            Galley & Cooking Facilities
          </div>
        </WizardSection.Header>
        
        <div className="space-y-4">
          <div>
            <label className="flex items-center justify-between">
              <div className="flex-1">
                <span className="text-sm font-medium text-gray-900">Galley Present</span>
                <p className="text-xs text-gray-500 mt-1">
                  Vessel has cooking facilities (affects fire equipment and detection requirements).
                  Galleys are treated as accommodation spaces per WBC3.
                </p>
              </div>
              <Switch
                checked={draftConfig.hasGalley || false}
                onChange={(checked) => {
                  if (!checked) {
                    // CRITICAL: When unchecking galley, clear ALL related fields
                    // These fields trigger FFE detectors in flattenVesselConfig.js

                    updateConfig({
                      hasGalley: false,
                      galleyHeatSource: null,
                      cookingType: null,  // Legacy field
                      hasCooking: false,
                      hasOpenFlame: false,
                      hasGasAppliance: false,
                      hasLiquidFuelAppliance: false,
                      gasAppliances: false,
                      openFlameProtection: {
                        blanket: false,
                        extraExtinguisher: false,
                        fixedSystem: false
                      },
                      fixedFFE: {
                        ...draftConfig.fixedFFE,
                        galley: false
                      }
                    });
                  } else {

                    updateConfig({ hasGalley: true });
                  }
                }}
              />
            </label>
          </div>

          {/* Galley as Accommodation Space info banner */}
          {draftConfig.hasGalley && !draftConfig.hasAccommodation && (
            <WizardSection.Callout variant="info" title="Galley Treated as Accommodation Space">
              <p className="text-sm">
                Under WBC3, galleys are classified as accommodation spaces (any enclosed space provided for use of persons on-board).
                A <strong>fire detector will be required</strong> even though "Accommodation Spaces" above is unchecked.
              </p>
              <p className="text-xs mt-2">
                You can keep these settings separate for clarity - the fire detection requirements will apply correctly based on your galley configuration.
              </p>
            </WizardSection.Callout>
          )}

          {draftConfig.hasGalley && (
            <>
              <WizardSection.Field
                label="Heat Source"
                help="Type of cooking equipment (affects fire and CO detector requirements)"
              >
                <WizardSection.Select
                  value={draftConfig.galleyHeatSource || 'electric'}
                  onChange={(e) => {
                    const value = e.target.value;

                    const needsProtection = ['gas','open_flame','liquid_fuel'].includes(value);
                    updateConfig({
                      galleyHeatSource: value,
                      hasCooking: true,
                      hasOpenFlame: needsProtection,
                      hasGasAppliance: value === 'gas',
                      hasLiquidFuelAppliance: value === 'liquid_fuel',
                      ...(needsProtection ? {} : {
                        // If no open flame, ensure protections are cleared in form state
                        openFlameProtection: { blanket: false, extraExtinguisher: false, fixedSystem: false }
                      })
                    });
                  }}
                >
                  <option value="electric">Electric/Induction</option>
                  <option value="gas">Gas (LPG/Butane)</option>
                  <option value="liquid_fuel">Liquid Fuel (Diesel/Paraffin)</option>
                </WizardSection.Select>
              </WizardSection.Field>

              {/* Open Flame Protection Options - show when gas or liquid_fuel selected */}
              {['gas', 'open_flame', 'liquid_fuel'].includes(draftConfig.galleyHeatSource) && (
                <>
                  <WizardSection.Callout variant="warning" title="WBC3 16.3.1 Open Flame Requirement">
                    <p className="text-sm">Every vessel with gas/open flame cooking must have:</p>
                    <ol className="mt-1 ml-4 list-decimal list-inside text-sm">
                      <li>One 5A/34B extinguisher (standard galley requirement - assumed present)</li>
                      <li>PLUS at least one of the additional protections below</li>
                    </ol>
                  </WizardSection.Callout>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-2">
                      Additional open-flame protection installed:
                    </label>
                    <p className="text-xs text-gray-500 mb-3">
                      Select which additional protection method(s) are installed:
                    </p>
                    <div className="space-y-3 ml-4">
                      <label className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          checked={draftConfig.openFlameProtection?.blanket || false}
                          onChange={(e) => updateConfig({ 
                            openFlameProtection: {
                              ...draftConfig.openFlameProtection,
                              blanket: e.target.checked
                            }
                          })}
                          className="h-4 w-4 rounded border-gray-300 text-cyan-600 focus:ring-cyan-600"
                        />
                        <span className="text-sm text-gray-700">Fire blanket</span>
                      </label>
                      
                      <label className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          checked={draftConfig.openFlameProtection?.extraExtinguisher || false}
                          onChange={(e) => updateConfig({ 
                            openFlameProtection: {
                              ...draftConfig.openFlameProtection,
                              extraExtinguisher: e.target.checked
                            }
                          })}
                          className="h-4 w-4 rounded border-gray-300 text-cyan-600 focus:ring-cyan-600"
                        />
                        <span className="text-sm text-gray-700">Additional extinguisher (8A/68B) - beyond the standard 5A/34B</span>
                      </label>
                      
                      <label className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          checked={draftConfig.openFlameProtection?.fixedSystem || false}
                          onChange={(e) => {
                            const isChecked = e.target.checked;
                            // Update open flame protection choice
                            updateConfig({ 
                              openFlameProtection: {
                                ...draftConfig.openFlameProtection,
                                fixedSystem: isChecked
                              },
                              // Also mirror to fixedFFE.galley (set both true and false)
                              fixedFFE: {
                                ...draftConfig.fixedFFE,
                                galley: isChecked
                              }
                            });
                          }}
                          className="h-4 w-4 rounded border-gray-300 text-cyan-600 focus:ring-cyan-600"
                        />
                        <span className="text-sm text-gray-700">Fixed galley suppression system (per 16.4)</span>
                      </label>
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </WizardSection.Card>

      {/* Hazards & Risks Section - Reorganized */}
      <WizardSection.Card>
        <WizardSection.Header>
          <div className="flex items-center">
            <AlertTriangle className="h-5 w-5 text-amber-500 mr-2" />
            Hazards & Risks
          </div>
        </WizardSection.Header>
        <p className="text-sm text-gray-600 mb-4">
          Review auto-detected hazards and identify any additional risks that affect fire detection and suppression requirements
        </p>
        
        {/* Auto-Detected Hazards */}
        {(() => {
          // Calculate auto-detected hazards
          const autoDetected = [];
          
          // Lithium batteries from propulsion config
          if (draftConfig.batteryType === 'lithium-ion') {
            autoDetected.push({
              name: 'Lithium Battery Systems',
              reason: 'Battery-electric/hybrid propulsion with lithium-ion batteries',
              requirement: 'Smoke, heat AND flame detectors required (WBC3 Annex 1)',
              field: 'hasLithiumBatteries'
            });
          }
          
          // Gas systems
          if (draftConfig.galleyHeatSource === 'gas' || draftConfig.hasLPGSystem) {
            autoDetected.push({
              name: 'Gas Appliances & Leak Risk',
              reason: draftConfig.galleyHeatSource === 'gas' 
                ? 'Gas cooking appliances in galley' 
                : 'LPG system installed',
              requirement: 'Hydrocarbon gas detector required (WBC3 15.6.4.1)',
              fields: ['gasAppliances', 'hasGasLeakRisk']
            });
          }
          
          // Exhaust leak risk
          const hasInboardEngine = draftConfig.enginePosition && draftConfig.enginePosition !== 'outboard';
          if (draftConfig.hasEnclosedSpaces && hasInboardEngine) {
            autoDetected.push({
              name: 'Exhaust Leak Risk',
              reason: 'Enclosed spaces with inboard engine',
              requirement: 'CO detectors required (WBC3 15.6.3.3)',
              field: 'hasExhaustLeakRisk'
            });
          }
          
          // Calculate config updates for detected hazards
          const hazardUpdates = useMemo(() => {
            const updates = {};
            let needsUpdate = false;
            
            // Set hasLithiumBatteries if battery-electric/hybrid with lithium
            if (draftConfig.batteryType === 'lithium-ion' && !draftConfig.hasLithiumBatteries) {
              updates.hasLithiumBatteries = true;
              needsUpdate = true;
            }
            
            // Set gas-related hazards
            const hasGasSystem = draftConfig.galleyHeatSource === 'gas' || draftConfig.hasLPGSystem;
            if (hasGasSystem && !draftConfig.gasAppliances) {
              updates.gasAppliances = true;
              updates.hasGasLeakRisk = true;
              needsUpdate = true;
            }
            
            // Set exhaust leak risk
            const hasExhaustRisk = draftConfig.hasEnclosedSpaces && hasInboardEngine;
            if (hasExhaustRisk && !draftConfig.hasExhaustLeakRisk) {
              updates.hasExhaustLeakRisk = true;
              needsUpdate = true;
            }
            
            return needsUpdate ? updates : null;
          }, [draftConfig.batteryType, draftConfig.galleyHeatSource, draftConfig.hasLPGSystem, 
              draftConfig.hasEnclosedSpaces, draftConfig.hasLithiumBatteries,
              draftConfig.gasAppliances, draftConfig.hasExhaustLeakRisk, hasInboardEngine]);
          
          // Apply hazard updates when they change
          useEffect(() => {
            if (hazardUpdates) {
              updateConfig(hazardUpdates);
            }
          }, [hazardUpdates]);
          
          return autoDetected.length > 0 ? (
            <div className="mb-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Auto-Detected Hazards</h4>
              <div className="space-y-2">
                {autoDetected.map((hazard, index) => (
                  <div key={index} className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <div className="flex items-start gap-2">
                      <div className="flex-shrink-0 w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center mt-0.5">
                        <span className="text-xs font-medium text-blue-600">✓</span>
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">{hazard.name}</p>
                        <p className="text-xs text-gray-600 mt-1">
                          <span className="font-medium">Detected because:</span> {hazard.reason}
                        </p>
                        <p className="text-xs text-blue-700 mt-1">
                          <span className="font-medium">Requirement:</span> {hazard.requirement}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null;
        })()}
        
        {/* Manual Risk Identification with Location Details */}
        <AdditionalRiskIdentification draftConfig={draftConfig} updateConfig={updateConfig} />
      </WizardSection.Card>

      {/* Help Information */}
      <WizardSection.Callout variant="info" title="What's Next?">
        After configuring spaces and accommodation, we'll set up safety equipment and systems 
        based on your vessel's configuration and operational requirements.
      </WizardSection.Callout>
    </WizardSection>
  );
};

export default StepSpaces;